import React, { useEffect, useRef, useState } from "react";

import PinInput from "react-pin-input";
import { Navigate, NavLink, useNavigate } from "react-router-dom";
import translator from "../Components/Extra/Translation/Translate";
import { ModalContext } from "../Components/Extra/Modal/ModalContext";
import { useAppProvider } from "../provider/AppProvider";
import AppAuthentication from "../Services/Authentication/AppAuthentication";
import useAuthentication from "../Services/Authentication/useAuthentication";
import AppProfileService from "../Services/User/Profile/AppProfile";
import alert_message from "../Services/Alert/AlertMessage";
import PhoneInput from "react-phone-input-2";
import UserService from "../Services/User/UserService";
import ReCAPTCHA from "react-google-recaptcha";

const CHECK_INTERVAL = 1000 * 1;

const useCountDown = (timer: number) => {
    const [timeLeft, setTimeLeft] = useState(timer);
    const [isRunning, setIsRunning] = useState(true);

    useEffect(() => {
        let timer: any;
        if (isRunning && timeLeft > 0) {
            // Décrémentation chaque seconde
            timer = setInterval(() => {
                setTimeLeft((prevTime) => prevTime - 1);
            }, CHECK_INTERVAL);
        } else if (timeLeft === 0) {
            // Stoppe le timer à 0
            setIsRunning(false);
        }

        return () => clearInterval(timer);
    }, [isRunning, timeLeft]);

    const restartTimer = () => {
        setTimeLeft(60);
        setIsRunning(true);
    };

    return { timeLeft, isRunning, setIsRunning, restartTimer };
};

export const VerifyPhoneForm = () => {
    const { translate } = translator();
    const { handleModal } = React.useContext(ModalContext);
    const { processing, setProcessing, finalizeProcessing } = useAppProvider();
    const [code, setCode] = useState<string>("");
    const { getInformationAsync } = AppAuthentication();
    const { user } = useAuthentication();
    const { timeLeft, isRunning, restartTimer } = useCountDown(60);

    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const [token, setToken] = useState<string | null>(null);

    const verifyPhone = () => {
        setProcessing(true);
        recaptchaRef.current?.reset();
        if (!token) return;
        const verifyPhone = AppProfileService.verifyPhone(code, token);
        alert_message
            .promise(
                translate("PhoneVerification", "Verify_In_Process"),
                verifyPhone,
                translate("PhoneVerification", "Verified_With_Success"),
            )
            .then(() => {
                handleModal();
            })
            .finally(() => {
                finalizeProcessing();
                getInformationAsync(true);
            });
    };
    const resendCode = () => {
        setProcessing(true);
        if (!token) return;
        recaptchaRef.current?.reset();
        const verifyPhone = AppProfileService.resendVeridicationCode(token);
        restartTimer();
        alert_message
            .promise(
                translate("PhoneVerification", "Send_code_in_progress"),
                verifyPhone,
                translate("PhoneVerification", "Code_sent_with_success"),
            )
            .finally(() => {
                finalizeProcessing();
                getInformationAsync(true);
            });
    };

    if (user?.phoneVerified) return <Navigate to={`dashboard`} />;

    return (
        <div className="bg-[#384146] auth-form opacity-90 shadow-lg z-10 rounded-xl px-4 md:!px-9 py-6 md:!py-10 space-y-6 w-full mx-auto">
            <div className="relative">
                <div className="intro-x mt-8">
                    <div className="intro-x mt-2 text-center text-slate-400 ">
                        {translate("PhoneVerification", "Indication")}
                    </div>
                    <div className="flex justify-center items  w-full">
                        <PinInput
                            length={6}
                            initialValue={code}
                            // secret
                            onComplete={(value) => {
                                setCode(value);
                            }}
                            type="numeric"
                            inputMode="number"
                            style={{ padding: "10px" }}
                            inputStyle={{
                                borderColor: "#1b253b",
                                borderRadius: "10px",
                                color: "white",
                            }}
                            inputFocusStyle={{ borderColor: "#053B3B" }}
                            autoSelect={true}
                            regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
                        />
                    </div>
                    <div className="w-full justify-center recap">
                        {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                            <>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={
                                        process.env.REACT_APP_RECAPTCHA_SITE_KEY
                                    }
                                    onChange={(t) => setToken(t)}
                                />
                            </>
                        )}
                    </div>
                    {isRunning && (
                        <div className="intro-x mt-2 text-center text-slate-400 ">
                            {translate("PhoneVerification", "TimeLeft", {
                                timeLeft: timeLeft,
                            })}
                        </div>
                    )}
                    <div className="flex justify-center items  w-full">
                        {!isRunning && (
                            <button
                                type="button"
                                className="btn btn-perfect mt-2 mr-2"
                                onClick={() => resendCode()}
                                disabled={processing || !token}
                            >
                                {translate("PhoneVerification", "ResendCode")}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-perfect mt-2 mr-2"
                            onClick={() => verifyPhone()}
                            disabled={code.length !== 6 || processing || !token}
                        >
                            {translate("PhoneVerification", "Verify")}
                        </button>
                    </div>
                    <div className="flex justify-center mt-7 space-x-2 w-full">
                        <NavLink
                            to="/set-phone-number"
                            className={"text-white text-center underline"}
                        >
                            {translate(
                                "PhoneVerification",
                                "Change_phone_number",
                            )}
                        </NavLink>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SetPhoneNumber = () => {
    const { getInformationAsync } = AppAuthentication();
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const { translate } = translator();
    const { user } = useAuthentication();
    const [phoneCountryCode, setPhoneCountryCode] = useState(
        user?.phoneCountryCode,
    );

    const navigate = useNavigate();
    const [phone, setPhone] = useState(user?.phone);
    const [processing, setProcessing] = useState<boolean>(false);

    const setPhoneNumber = async () => {
        if (!phoneCountryCode || !phone || !token) return;
        recaptchaRef.current?.reset();
        setProcessing(true);
        const set_phone = UserService.setPhoneNumber(
            phoneCountryCode,
            phone,
            token,
        );
        alert_message
            .promise(
                translate("PhoneVerification", "Send_code_in_progress"),
                set_phone,
            )
            .then(() => {
                getInformationAsync(true).then(() => {
                    navigate("/verify-phone");
                });
            })
            .catch(() => {
                setProcessing(false);
            });
    };

    return (
        <div className="bg-[#384146] auth-form opacity-90 shadow-lg z-10 rounded-xl px-4 md:!px-9 py-6 md:!py-10 space-y-6 w-full mx-auto">
            <div className="relative">
                <div className="intro-x mt-8">
                    <div className="intro-x mt-2 text-center text-slate-400 ">
                        {translate("PhoneVerification", "Your_phone_number")}
                    </div>
                    <div className="flex justify-center items ">
                        <div className="relative mt-4  ">
                            <PhoneInput
                                country={"de"}
                                // value={phoneCountryCode + (phone || "")}
                                inputClass="!bg-clisha-600 !text-white placeholder:!text-slate-300 !py-3 !w-full !border-transparent !rounded-xl !h-[45px] "
                                dropdownClass="!bg-clisha-600 !text-white placeholder:!text-slate-300 !border-transparent "
                                buttonClass=" !rounded-xl !bg-clisha-600 !border-transparent"
                                searchClass="!bg-clisha-600 !text-white placeholder:!text-slate-300 !border-transparent "
                                excludeCountries={[
                                    "af",
                                    "ir",
                                    "iq",
                                    "kp",
                                    "ly",
                                    "ss",
                                    "sd",
                                    "sy",
                                    "ye",
                                ]}
                                countryCodeEditable={false}
                                enableLongNumbers={15}
                                enableSearch={true}
                                onChange={(phone, data) => {
                                    if (data && phone.length > 7) {
                                        const dialCode = (data as any).dialCode;
                                        if (dialCode) {
                                            setPhoneCountryCode(dialCode);
                                            const number = phone
                                                .replace("+", "")
                                                .replace(dialCode, "");
                                            setPhone(number);
                                        }
                                    }
                                }}
                                specialLabel={translate(
                                    "PhoneVerification",
                                    "Phone_number",
                                )}
                                containerClass="relative 0"
                            />
                        </div>
                    </div>
                    <div className="w-full justify-center recap py-5">
                        {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                            <>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={
                                        process.env.REACT_APP_RECAPTCHA_SITE_KEY
                                    }
                                    onChange={(t) => setToken(t)}
                                />
                            </>
                        )}
                    </div>
                    <div className="flex justify-center items  w-full">
                        <button
                            type="button"
                            className="btn btn-perfect mt-2 mr-2"
                            onClick={() => setPhoneNumber()}
                            disabled={
                                processing ||
                                !token ||
                                isNaN(Number(phoneCountryCode)) ||
                                isNaN(Number(phone))
                            }
                        >
                            {translate("PhoneVerification", "Validate")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function AppPhoneChecker({
    children,
}: {
    children: React.ReactNode;
}) {
    return <>{children}</>;
}
